import classnames from 'classnames';
import {
  useEffect,
  useState,
  type Dispatch,
  type FunctionComponent,
  type SetStateAction,
} from 'react';
import { useDispatch } from 'react-redux';

import { AlertMessage } from '@/core/tamagoshi/AlertMessage/AlertMessage';

import { useFlashMessage } from '../hooks/useFlashMessage';
import {
  AlertSeverityType,
  closeCurrentAlertMessage,
  handleAlertMessageAction,
  type AlertMessageActionConfig,
  type AlertMessage as AlertMessageInterface,
} from './FlashMessages.actions';
import { close } from './translations';
import { queueToList } from './utils/queueToList';

import styles from './FlashMessages.module.scss';

export interface FlashMessagesProps {
  className?: string;
  onMessageEnter?: () => void;
  onMessageExit?: () => void;
}

const nullAlert: AlertMessageInterface = {
  id: 'null',
  severity: AlertSeverityType.INFO,
  title: '',
  message: '',
  actionText: '',
  actionUrl: '',
};
/**
 * FlashMessages component.
 */
export const FlashMessages: FunctionComponent<FlashMessagesProps> = ({
  className,
  onMessageEnter,
  onMessageExit,
}) => {
  const [selectAlertContainer, setSelectedAlertContainer]: [
    number,
    Dispatch<SetStateAction<any>>,
  ] = useState(0);
  const [alertObjA, setAlertObjA] = useState(nullAlert);
  const [alertObjB, setAlertObjB] = useState(nullAlert);

  const { alertMessages } = useFlashMessage();
  const dispatch = useDispatch();
  const alertList = queueToList(alertMessages);

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    const focusedContainer = selectAlertContainer === 0 ? 1 : 0;
    setSelectedAlertContainer(focusedContainer);
    const focusedAlertMessage: AlertMessageInterface = alertList[0];
    if (focusedContainer === 0) {
      setAlertObjA(focusedAlertMessage);
    } else {
      setAlertObjB(focusedAlertMessage);
    }
  }, [alertMessages]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const onCloseCurrentAlert = () => {
    dispatch(closeCurrentAlertMessage());
  };

  const onActionClick = (
    actionConfig: AlertMessageActionConfig | undefined,
  ) => {
    dispatch(handleAlertMessageAction(actionConfig));
    dispatch(closeCurrentAlertMessage());
  };

  return (
    <>
      {[alertObjA, alertObjB].map((alertObj: AlertMessageInterface, idx) =>
        alertObj &&
        !(alertList.length === 0) &&
        selectAlertContainer === idx ? (
          <AlertMessage
            key={`alertContainer_${idx.toString()}`}
            className={classnames([
              styles.alertMessage,
              styles.visible,
              className,
            ])}
            severity={alertObj.severity}
            title={alertObj.title}
            actionText={alertObj.actionText}
            actionUrl={alertObj.actionUrl}
            onActionClick={() => onActionClick(alertObj.actionConfig)}
            onClose={onCloseCurrentAlert}
            closeButtonLabel={close}
            data-testid="flash-message"
            fullWidth
            onMounted={onMessageEnter}
            onUnmounted={onMessageExit}
          >
            {alertObj.message}
          </AlertMessage>
        ) : null,
      )}
    </>
  );
};

FlashMessages.displayName = 'FlashMessages';
